<template>
  <div>
    <DeliveryPromiseCard
      :display-format
      :express="expressShipping"
      :express-days
      :free="freeShipping"
      :free-days
      :is-express-before-christmas="isExpressDeliveryBeforeChristmas"
      :is-standard-before-christmas="isFreeDeliveryBeforeChristmas"
      stacked
    >
      <template v-if="slots.title" #title>
        <slot name="title" />
      </template>

      <template v-if="showPostalCode" #extra-info>
        <DeliveryPostalCodeTrigger
          boxed-icon
          entry-point="pdp"
          :tracking-context="props.trackingContext"
        />
      </template>
    </DeliveryPromiseCard>

    <DeliveryPostalCodeModal
      v-if="showPostalCode"
      entry-point="pdp"
      :tracking-context="props.trackingContext"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import { useFetchShipping } from '~/scopes/product-core/composables/useFetch/useFetchShipping/useFetchShipping'
import { useDeliveryBeforeChristmas } from '~/scopes/shipping/composables/useDeliveryBeforeChristmas/useDeliveryBeforeChristmas'
import { useDeliveryDatesFromShipping } from '~/scopes/shipping/composables/useDeliveryDatesFromShipping/useDeliveryDatesFromShipping'

import DeliveryPostalCodeModal from '../DeliveryPostalCodeModal/DeliveryPostalCodeModal.vue'
import DeliveryPostalCodeTrigger from '../DeliveryPostalCodeModal/DeliveryPostalCodeTrigger.vue'
import DeliveryPromiseCard from '../DeliveryPromiseCard/DeliveryPromiseCard.vue'

const slots = useSlots()

const props = defineProps<{
  shipping: NonNullable<GetPickersResponse['selectedOffer']>['shipping']
  trackingContext?: 'web' | 'mweb' | 'recap'
}>()

const experiments = useExperiments()

const shippingResponse = useFetchShipping()
const defaultShipping = useDeliveryDatesFromShipping(() => props.shipping)
const shippingDates = useDeliveryDatesFromShipping(
  () => shippingResponse.data?.value ?? null,
)
const {
  isStandardDeliveryBeforeChristmas: isStandardDeliveryBeforeChristmasResponse,
  isExpressDeliveryBeforeChristmas: isExpressDeliveryBeforeChristmasResponse,
} = useDeliveryBeforeChristmas(() => shippingResponse.data?.value ?? null)
const {
  isStandardDeliveryBeforeChristmas: isStandardDeliveryBeforeChristmasDefault,
  isExpressDeliveryBeforeChristmas: isExpressDeliveryBeforeChristmasDefault,
} = useDeliveryBeforeChristmas(() => props.shipping)

const showPostalCode = computed(
  () => experiments['experiment.postalCodePromise'] === 'postalCodeEnabled',
)

const displayFormat = computed(() =>
  experiments['experiment.deliveryPromiseDisplay'] ===
  'deliveryPromiseDaysCount'
    ? 'daysCount'
    : 'dateRange',
)

const freeShipping = computed(
  () => shippingDates.value.free || defaultShipping.value.free,
)
const expressShipping = computed(() => {
  // If the free shipping date is set, it means we have a postal code set, so we need to use the express shipping date
  if (shippingDates.value.free) {
    return shippingDates.value.express
  }

  // If we have no postal code set, we can use the default shipping date.
  return defaultShipping.value.express
})
const freeDays = computed(
  () => shippingDates.value.freeDays || defaultShipping.value.freeDays,
)
const expressDays = computed(() => {
  if (shippingDates.value.free) {
    return shippingDates.value.expressDays
  }

  return defaultShipping.value.expressDays
})
const isFreeDeliveryBeforeChristmas = computed(
  () =>
    isStandardDeliveryBeforeChristmasResponse.value ||
    isStandardDeliveryBeforeChristmasDefault.value,
)
const isExpressDeliveryBeforeChristmas = computed(
  () =>
    isExpressDeliveryBeforeChristmasResponse.value ||
    isExpressDeliveryBeforeChristmasDefault.value,
)
</script>
